import Axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
  Table,
  message,
  Button,
  Modal,
  DatePicker,
  Select,
  Input,
  Descriptions,
  Divider,
  Space,
  Tooltip,
  Spin,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
// テーブル強化 PJ Ph.3 拡大: 列リサイズ + ソート + 省略
import {
  PageContainer,
  StatusBadge,
  useResizableColumns,
  ellipsisColumn,
  textSorter,
  dateSorter,
  numberSorter,
} from '../../common';

const { confirm } = Modal;
const { Option } = Select;

const MailReservateListPage = (props) => {
  const { user } = props;
  const [mailReservates, setMailReservates] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editSendDateTime, setEditSendDateTime] = useState(null);
  const [editMtId, setEditMtId] = useState('');
  const [editMtName, setEditMtName] = useState('');
  const [editCc, setEditCc] = useState('');
  const [editBcc, setEditBcc] = useState('');
  const [editIsAnswerd, setEditIsAnswerd] = useState('');
  const [editReadOnly, setEditReadOnly] = useState(false); // 送信済み予約は読み取り専用（詳細表示）で開く
  // プレビュー（サーベイシーズン変数のみ反映。ユーザー変数は {$...$} のまま）
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mailTemplates, setMailTemplates] = useState([]);
  const [surveyList, setSurveyList] = useState([]);
  const [editSurveyCode, setEditSurveyCode] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  // 送付先（対象者）表示用。ユーザー選択/CSV/管理者送付でも「誰が対象か」を確認できるようにする。
  const [editRecipients, setEditRecipients] = useState([]);
  const [editRecipientsLoading, setEditRecipientsLoading] = useState(false);
  // true = 配信時点で対象が確定する種別（配信前の全体/リマインド/回答済）→ 人数変動の注意書きを表示
  const [editRecipientsDynamic, setEditRecipientsDynamic] = useState(false);
  const usr = useSelector((state) => state.user);
  // 検索条件（企業名・シーズン名・テンプレート名 = 部分一致、実施区分・配信状態 = 絞り込み）
  const [search, setSearch] = useState({
    groupName: '',
    seasonName: '',
    mtName: '',
    isAnswerd: 'all',
    mailStatus: 'all',
  });

  const readDB = async () => {
    try {
      if (!usr.userData) return null;
      if (usr.userData.isAuth) {
        const res = await Axios.get('/api/sendMail/reservate');
        if (res.data.success) {
          setMailReservates(res.data.mailReservates);
        } else {
          throw new Error('メール送信予約取得に失敗しました。');
        }
      } else {
        throw new Error('ログイン前です。');
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    readDB();
  }, [usr]);

  // メール送信状態を表示する関数
  // 企画 デザイン修正依頼1 item 10.4: メール配信状況 = 四角枠・ソリッド・白文字（variant="solid"）。
  //   予約中=warning(#E8A30A) / 送信完了=success(#4CAF67) / 送信エラー=error(#D14343)。
  const getStatusTag = (status, record) => {
    // 失敗系（一部送信・送信エラー）はマウスホバーで失敗原因をツールチップ表示（Ph.3）
    const reason = record?.send_result?.last_error;
    const withReason = (badge) =>
      reason ? (
        <Tooltip title={reason}>
          <span>{badge}</span>
        </Tooltip>
      ) : (
        badge
      );
    switch (status) {
      case 0:
        return <StatusBadge tone="warning" variant="solid">予約中</StatusBadge>;
      case 1:
        return <StatusBadge tone="info" variant="solid">処理中</StatusBadge>;
      case 5:
        return <StatusBadge tone="success" variant="solid">送信完了</StatusBadge>;
      case 7:
        // 一部送信: 1,000名超のチャンク分割送信で一部チャンクが失敗
        return withReason(
          <StatusBadge tone="warning" variant="solid">一部送信</StatusBadge>
        );
      case 999:
        return withReason(
          <StatusBadge tone="error" variant="solid">送信エラー</StatusBadge>
        );
      default:
        return <StatusBadge tone="neutral" variant="solid">不明</StatusBadge>;
    }
  };

  // 日時フォーマット関数
  const formatDateTime = (dateTime) => {
    const date = new Date(dateTime);
    return (
      date.getFullYear() +
      '/' +
      ('0' + (date.getMonth() + 1)).slice(-2) +
      '/' +
      ('0' + date.getDate()).slice(-2) +
      ' ' +
      ('0' + date.getHours()).slice(-2) +
      ':' +
      ('0' + date.getMinutes()).slice(-2)
    );
  };

  // 実施区分を表示する関数
  const getAnswerStatus = (isAnswerd) => {
    switch (isAnswerd) {
      case '9':
        return '全体';
      case '0':
        return 'リマインド';  // 未回答はリマインドとして表示
      case '5':
        return '回答済み';
      case '99':
        return 'ユーザー選択';
      case 'CSV':
        return 'CSV';
      case 'SEASON_ADMIN':
        return 'シーズン管理者';
      case 'COMPANY_ADMIN':
        return '企業管理者';
      default:
        return '不明';
    }
  };

  // 予約削除処理
  const deleteReservate = async (id) => {
    try {
      const res = await Axios.delete(`/api/sendMail/reservate/${id}`);
      if (res.data.success) {
        message.success('予約が削除されました');
        readDB(); // リストを再読み込み
      } else {
        message.error('予約削除に失敗しました');
      }
    } catch (error) {
      console.log(error);
      message.error('予約削除で問題が発生しました');
    }
  };

  // 失敗分のみ再送信処理（一部送信=7 / 送信エラー=999 が対象。成功者には再送信しない）
  const resendFailed = async (id) => {
    try {
      message.loading({ content: '失敗分を再送信中...', key: 'resend' });
      const res = await Axios.post(`/api/sendMail/reservate/${id}/resend-failed`);
      if (res.data.success) {
        message.success({
          content: res.data.message || '失敗分を再送信しました',
          key: 'resend',
          duration: 4,
        });
        readDB(); // リストを再読み込み
      } else {
        message.error({
          content: res.data.error || '再送信に失敗しました',
          key: 'resend',
          duration: 4,
        });
      }
    } catch (error) {
      console.log(error);
      message.error({
        content:
          error.response?.data?.error || '再送信で問題が発生しました',
        key: 'resend',
        duration: 4,
      });
    }
  };

  // 失敗分再送信の確認モーダル
  const showResendConfirm = (record) => {
    const failedCount = record.send_result?.failed_count || 0;
    confirm({
      title: '失敗分のみ再送信しますか？',
      content: `失敗した ${failedCount}名のみに再送信します（送信済みの方には再送信しません）。`,
      okText: 'はい',
      cancelText: 'いいえ',
      onOk() {
        resendFailed(record._id);
      },
    });
  };

  // 編集モーダルを開く
  const openEditModal = async (record, readOnly = false) => {
    setEditReadOnly(readOnly);
    setEditingRecord(record);
    setEditSendDateTime(dayjs(record.send_date_time));
    setEditMtId(record.mt_id || '');
    setEditMtName(record.mt_name || '');

    const fc = record.filter_conditions || {};
    const ccEmails = (fc.cc || []).map((c) => c.email).join(';');
    const bccEmails = (fc.bcc || []).map((b) => b.email).join(';');
    setEditCc(ccEmails);
    setEditBcc(bccEmails);
    setEditIsAnswerd(record.isAnswerd || '');

    // 送付先（対象者）を取得（種別ごとにサーバー側で解決: 99=スナップショット / 管理者・リマインド=live / CSV=personalizations）
    setEditRecipients([]);
    setEditRecipientsDynamic(false);
    setEditRecipientsLoading(true);
    Axios.get(`/api/sendMail/reservate/${record._id}/recipients`)
      .then((res) => {
        if (res.data.success) {
          setEditRecipients(res.data.recipients || []);
          setEditRecipientsDynamic(!!res.data.dynamic);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setEditRecipientsLoading(false));

    // テンプレート/サーベイをロードしてからモーダルを開く
    try {
      const [mtRes, sRes] = await Promise.all([
        Axios.get('/api/mailTemplate/'),
        Axios.get('/api/survey/'),
      ]);
      const templates =
        mtRes.data.success && mtRes.data.mailTemplate
          ? mtRes.data.mailTemplate
          : [];
      const surveys =
        sRes.data.success && sRes.data.survey ? sRes.data.survey : [];
      setMailTemplates(templates);
      setSurveyList(surveys);

      // 選択されているテンプレートのsurvey_codeをサーベイ種類として設定
      const currentTemplate = templates.find((t) => t._id === record.mt_id);
      const surveyCode = currentTemplate ? currentTemplate.survey_code : '';
      setEditSurveyCode(surveyCode || '');
    } catch (error) {
      console.log(error);
      setEditSurveyCode('');
    }

    setEditModalOpen(true);
  };

  // 編集保存処理
  const handleEditSave = async () => {
    if (!editingRecord) return;

    if (!editSendDateTime || editSendDateTime.isBefore(dayjs())) {
      message.error('送信日時は未来の日時を指定してください');
      return;
    }

    if (!editMtId) {
      message.error('テンプレートを選択してください');
      return;
    }

    const ccArr = editCc
      ? editCc
          .split(';')
          .filter((e) => e.trim())
          .map((e) => ({ email: e.trim() }))
      : [];
    const bccArr = editBcc
      ? editBcc
          .split(';')
          .filter((e) => e.trim())
          .map((e) => ({ email: e.trim() }))
      : [];

    // 実施区分が変更不可（'CSV' / '99'=ユーザー選択 / 送付先=管理者）の予約では
    // Select が disabled で値が変わらないため、不変の isAnswerd は送信しない
    // （バックエンドは isAnswerd===undefined なら検証をスキップする）。
    // 日時・CC/BCC のみ更新される。
    const isAnswerdLocked =
      editingRecord.isAnswerd === 'CSV' ||
      editingRecord.isAnswerd === '99' ||
      editingRecord.isAnswerd === 'SEASON_ADMIN' ||
      editingRecord.isAnswerd === 'COMPANY_ADMIN';

    const payload = {
      send_date_time: editSendDateTime.toDate(),
      mt_id: editMtId,
      mt_name: editMtName,
      cc: ccArr,
      bcc: bccArr,
    };
    if (!isAnswerdLocked) {
      payload.isAnswerd = editIsAnswerd;
    }

    setEditLoading(true);
    try {
      const res = await Axios.patch(
        `/api/sendMail/reservate/${editingRecord._id}`,
        payload
      );
      if (res.data.success) {
        message.success('予約内容を更新しました');
        setEditModalOpen(false);
        setEditingRecord(null);
        readDB();
      } else {
        message.error(res.data.error || '更新に失敗しました');
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.error || '更新で問題が発生しました';
      message.error(errMsg);
    } finally {
      setEditLoading(false);
    }
  };

  // 編集キャンセル
  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingRecord(null);
  };

  // プレビューを開く（サーベイシーズン変数を反映したメール文をサーバーで生成）
  // 取得できてからモーダルを開く。
  // 以前は先にモーダルを開いてから取得していたため、テンプレート削除済みなど
  // 必ず失敗するケースでは一瞬開いてすぐ閉じる見え方になっていた。
  // 取得中はボタン自体が loading になるので、画面が無反応に見えることはない。
  const openPreview = async (record) => {
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const res = await Axios.get(
        `/api/sendMail/reservate/${record._id}/preview`
      );
      if (res.data.success) {
        setPreviewData(res.data);
        setPreviewOpen(true);
      } else {
        message.error(res.data.error || 'プレビューの取得に失敗しました');
        setPreviewOpen(false); // 念のため（開いた状態での再取得に失敗した場合）
      }
    } catch (error) {
      console.log(error);
      message.error(
        error.response?.data?.error ||
          'プレビューの取得で問題が発生しました'
      );
      setPreviewOpen(false); // 念のため
    } finally {
      setPreviewLoading(false);
    }
  };

  // プレビュー内「テンプレを修正する」→ 別タブでテンプレート編集画面を開く。
  // 予約一覧のタブはそのまま残し、編集後にプレビューを開き直せば最新が反映される。
  const goEditTemplate = () => {
    const mtId = previewData?.mailTemplate?._id;
    if (mtId) {
      window.open(`/maketemplate?mt=${mtId}`, '_blank');
    }
  };

  // 削除確認ダイアログ
  const showDeleteConfirm = (id) => {
    confirm({
      title: 'メール配信予約を削除しますか？',
      content: 'この操作は取り消すことができません。',
      okText: 'はい',
      okType: 'danger',
      cancelText: 'いいえ',
      onOk() {
        deleteReservate(id);
      },
    });
  };

  // 検索条件をクリア
  const clearSearch = () => {
    setSearch({
      groupName: '',
      seasonName: '',
      mtName: '',
      isAnswerd: 'all',
      mailStatus: 'all',
    });
  };

  // 絞り込み後の一覧（企業名・シーズン名・テンプレート名は部分一致、実施区分・配信状態は完全一致）
  const filteredMailReservates = mailReservates.filter((r) => {
    const byGroup =
      !search.groupName ||
      (r.group_name || '')
        .toLowerCase()
        .includes(search.groupName.toLowerCase());
    const bySeason =
      !search.seasonName ||
      (r.season_name || '')
        .toLowerCase()
        .includes(search.seasonName.toLowerCase());
    const byMt =
      !search.mtName ||
      (r.mt_name || '').toLowerCase().includes(search.mtName.toLowerCase());
    const byAnswerd =
      search.isAnswerd === 'all' || r.isAnswerd === search.isAnswerd;
    const byStatus =
      search.mailStatus === 'all' ||
      String(r.mail_send_status) === search.mailStatus;
    return byGroup && bySeason && byMt && byAnswerd && byStatus;
  });

  // テーブル強化 PJ Ph.3 拡大: <Column> → columns 配列。テキスト列はソート + 省略、
  // 日付/状態列はソート、バッジ/操作列は既存表示を維持。
  const rawColumns = [
    {
      title: '送信予定日時',
      dataIndex: 'send_date_time',
      key: 'send_date_time',
      width: 160,
      sorter: dateSorter('send_date_time'),
      render: (text) => formatDateTime(text),
    },
    ellipsisColumn({
      title: '企業名',
      dataIndex: 'group_name',
      key: 'group_name',
      width: 160,
      sorter: textSorter('group_name'),
      render: (text) => text || '-',
    }),
    ellipsisColumn({
      title: 'サーベイシーズン名',
      dataIndex: 'season_name',
      key: 'season_name',
      width: 200,
      sorter: textSorter('season_name'),
      render: (text) => text || '-',
    }),
    ellipsisColumn({
      title: '設問セット名',
      dataIndex: 'set_name',
      key: 'set_name',
      width: 160,
      sorter: textSorter('set_name'),
      render: (text) => text || '-',
    }),
    ellipsisColumn({
      title: 'テンプレート名',
      dataIndex: 'mt_name',
      key: 'mt_name',
      width: 160,
      sorter: textSorter('mt_name'),
    }),
    {
      title: '実施区分',
      dataIndex: 'isAnswerd',
      key: 'isAnswerd',
      width: 120,
      align: 'center',
      sorter: textSorter('isAnswerd'),
      render: (text) => {
        const status = getAnswerStatus(text);
        // 企画 デザイン修正依頼1 item 10.3 / 修正依頼2 item 3 の配色（既存維持）。
        let tone = 'neutral';
        let toneStyle;
        switch (status) {
          case '全体':
            tone = 'processing';
            break;
          case 'リマインド':
            tone = 'info';
            break;
          case '回答済み':
            tone = 'success';
            break;
          case 'ユーザー選択':
            tone = 'warning';
            break;
          case 'CSV':
            toneStyle = {
              background: 'var(--grey-20)',
              color: 'var(--grey-65)',
            };
            break;
        }
        return (
          <StatusBadge tone={tone} style={toneStyle}>
            {status}
          </StatusBadge>
        );
      },
    },
    {
      title: 'メール配信状態',
      dataIndex: 'mail_send_status',
      key: 'mail_send_status',
      width: 140,
      align: 'center',
      sorter: numberSorter('mail_send_status'),
      render: (text, record) => getStatusTag(text, record),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (text, record) => (
        <Space size="small" wrap>
          {record.mail_send_status === 0 ? (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record, false)}
            >
              編集
            </Button>
          ) : (
            // 送信済み（処理中/送信完了/一部送信/送信エラー）は設定内容を読み取り専用で確認
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openEditModal(record, true)}
            >
              詳細
            </Button>
          )}
          <Button
            danger
            size="small"
            onClick={() => showDeleteConfirm(record._id)}
            disabled={record.mail_send_status !== 0}
          >
            予約削除
          </Button>
          {(record.mail_send_status === 7 || record.mail_send_status === 999) &&
            record.send_result?.failed_count > 0 && (
              <Button
                size="small"
                onClick={() => showResendConfirm(record)}
              >
                失敗分再送信
              </Button>
            )}
        </Space>
      ),
    },
  ];

  const { columns, components, scroll, wrapRef } = useResizableColumns(rawColumns);

  return (
    <PageContainer fluid>
      <h2>配信一覧</h2>
      {/* 検索ツール（画面上部に一列） */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          alignItems: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        <Input
          placeholder="企業名で検索"
          allowClear
          value={search.groupName}
          onChange={(e) => setSearch({ ...search, groupName: e.target.value })}
          style={{ width: 200 }}
        />
        <Input
          placeholder="サーベイシーズン名で検索"
          allowClear
          value={search.seasonName}
          onChange={(e) =>
            setSearch({ ...search, seasonName: e.target.value })
          }
          style={{ width: 220 }}
        />
        <Input
          placeholder="テンプレート名で検索"
          allowClear
          value={search.mtName}
          onChange={(e) => setSearch({ ...search, mtName: e.target.value })}
          style={{ width: 200 }}
        />
        <Select
          value={search.isAnswerd}
          onChange={(v) => setSearch({ ...search, isAnswerd: v })}
          style={{ width: 180 }}
          options={[
            { value: 'all', label: '実施区分：すべて' },
            { value: '9', label: '全体' },
            { value: '0', label: 'リマインド' },
            { value: '5', label: '回答済み' },
            { value: '99', label: 'ユーザー選択' },
            { value: 'CSV', label: 'CSV' },
          ]}
        />
        <Select
          value={search.mailStatus}
          onChange={(v) => setSearch({ ...search, mailStatus: v })}
          style={{ width: 180 }}
          options={[
            { value: 'all', label: '配信状態：すべて' },
            { value: '0', label: '予約中' },
            { value: '1', label: '処理中' },
            { value: '5', label: '送信完了' },
            { value: '7', label: '一部送信' },
            { value: '999', label: '送信エラー' },
          ]}
        />
        <Button onClick={clearSearch}>クリア</Button>
      </div>
      <div ref={wrapRef}>
        <Table
          columns={columns}
          components={components}
          scroll={scroll}
          dataSource={filteredMailReservates}
          size="middle"
          rowKey="_id"
          pagination={{
            pageSize: 100,
            defaultPageSize: 100,
          }}
        />
      </div>

      {/* 編集モーダル */}
      <Modal
        title={editReadOnly ? 'メール予約詳細' : 'メール予約編集'}
        open={editModalOpen}
        onOk={handleEditSave}
        onCancel={handleEditCancel}
        okText="保存"
        cancelText="キャンセル"
        confirmLoading={editLoading}
        width={700}
        destroyOnHidden
        footer={
          editReadOnly
            ? [
                <Button key="close" onClick={handleEditCancel}>
                  閉じる
                </Button>,
              ]
            : undefined
        }
      >
        {editingRecord && (
          <>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="企業名">
                {editingRecord.group_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="サーベイシーズン">
                {editingRecord.season_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="設問セット">
                {editingRecord.set_name || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                実施区分
                <span style={{ color: 'var(--red-default)' }}> *</span>
              </div>
              <Select
                style={{ width: '100%' }}
                value={editIsAnswerd || undefined}
                onChange={(val) => setEditIsAnswerd(val)}
                placeholder="実施区分を選択"
                disabled={
                  editReadOnly ||
                  editingRecord.isAnswerd === 'CSV' ||
                  editingRecord.isAnswerd === '99' ||
                  editingRecord.isAnswerd === 'SEASON_ADMIN' ||
                  editingRecord.isAnswerd === 'COMPANY_ADMIN'
                }
              >
                <Option value="9">全体</Option>
                <Option value="0">リマインド（未回答のみ）</Option>
                <Option value="5">回答済み</Option>
                {editingRecord.isAnswerd === 'CSV' && (
                  <Option value="CSV">CSV</Option>
                )}
                {/* ユーザー選択は対象者リストが本モーダルで編集できないため変更不可 */}
                {editingRecord.isAnswerd === '99' && (
                  <Option value="99">ユーザー選択</Option>
                )}
                {/* 送付先が管理者の予約は種別変更不可（表示のみ） */}
                {editingRecord.isAnswerd === 'SEASON_ADMIN' && (
                  <Option value="SEASON_ADMIN">シーズン管理者</Option>
                )}
                {editingRecord.isAnswerd === 'COMPANY_ADMIN' && (
                  <Option value="COMPANY_ADMIN">企業管理者</Option>
                )}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                送信予定日時
                <span style={{ color: 'var(--red-default)' }}> *</span>
              </div>
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                value={editSendDateTime}
                onChange={(val) => setEditSendDateTime(val)}
                style={{ width: 260 }}
                placeholder="送信予定日時を選択"
                disabled={editReadOnly}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                サーベイ種類
                <span style={{ color: 'var(--red-default)' }}> *</span>
              </div>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="サーベイ種類を選択"
                optionFilterProp="children"
                disabled={editReadOnly}
                value={editSurveyCode || undefined}
                onChange={(val) => {
                  setEditSurveyCode(val);
                  // サーベイ変更時、テンプレートが対応しなければリセット
                  const stillValid = mailTemplates.find(
                    (t) => t._id === editMtId && t.survey_code === val
                  );
                  if (!stillValid) {
                    setEditMtId('');
                    setEditMtName('');
                  }
                }}
                filterOption={(input, option) =>
                  option.children
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {surveyList.map((s) => (
                  <Option value={s.survey_code} key={s._id}>
                    {s.survey_name}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                メールテンプレート
                <span style={{ color: 'var(--red-default)' }}> *</span>
              </div>
              <Space.Compact style={{ width: '100%' }}>
                {editReadOnly ? (
                  <Input
                    style={{ flex: 1, minWidth: 0 }}
                    disabled
                    value={
                      mailTemplates.some((t) => t._id === editMtId)
                        ? editMtName ||
                          mailTemplates.find((t) => t._id === editMtId)?.mt_name
                        : 'メールテンプレが削除されています'
                    }
                  />
                ) : (
                  <Select
                    showSearch
                    style={{ flex: 1, minWidth: 0 }}
                    placeholder="テンプレート選択"
                    optionFilterProp="children"
                    value={editMtId || undefined}
                    onChange={(val) => {
                      setEditMtId(val);
                      const found = mailTemplates.find((t) => t._id === val);
                      if (found) setEditMtName(found.mt_name);
                    }}
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    disabled={!editSurveyCode}
                  >
                    {mailTemplates
                      .filter((t) => t.survey_code === editSurveyCode)
                      .map((t) => (
                        <Option value={t._id} key={t._id}>
                          {t.mt_name}
                        </Option>
                      ))}
                  </Select>
                )}
                <Button
                  icon={<FileTextOutlined />}
                  loading={previewLoading}
                  style={{ flexShrink: 0 }}
                  onClick={() => openPreview(editingRecord)}
                >
                  プレビュー
                </Button>
              </Space.Compact>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>CC</div>
              <Input
                placeholder="メールアドレスを「;」区切りで入力してください"
                allowClear
                value={editCc}
                onChange={(e) => setEditCc(e.target.value)}
                disabled={editReadOnly}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>BCC</div>
              <Input
                placeholder="メールアドレスを「;」区切りで入力してください"
                allowClear
                value={editBcc}
                onChange={(e) => setEditBcc(e.target.value)}
                disabled={editReadOnly}
              />
            </div>

            {/* 送付先（対象者）一覧: どの実施区分でも「誰が対象か」を確認できる。
                全体/リマインド/回答済（配信前）は配信時点で確定するため注意書きを表示。
                大人数（全社 1000〜2000名等）でも描画が重くならないよう一覧は上限表示。 */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 4, fontWeight: 'bold' }}>
                送付先（対象者）
                {!editRecipientsLoading && (
                  <span style={{ fontWeight: 'normal', marginLeft: 8 }}>
                    {editRecipients.length}名
                  </span>
                )}
              </div>
              {!editRecipientsLoading && editRecipientsDynamic && (
                <div
                  style={{ color: '#fa8c16', fontSize: 12, marginBottom: 6 }}
                >
                  ＊対象者は配信時点で確定するため、人数変動の可能性があります。
                </div>
              )}
              {editRecipientsLoading ? (
                <div style={{ textAlign: 'center', padding: 12 }}>
                  <Spin size="small" />
                </div>
              ) : editRecipients.length === 0 ? (
                <div style={{ color: 'var(--gray-600, #888)' }}>
                  対象者情報がありません。
                </div>
              ) : (
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid var(--gray-300, #d9d9d9)',
                    borderRadius: 6,
                    padding: '6px 10px',
                    // 入力欄と誤認されないよう非活性風のグレー背景にする
                    background: 'var(--gray-100, #f5f5f5)',
                    cursor: 'default',
                  }}
                >
                  {editRecipients.slice(0, 300).map((r, i) => (
                    <div key={i} style={{ padding: '2px 0' }}>
                      {r.name || '(氏名なし)'}
                      {r.email ? (
                        <span
                          style={{
                            // グレー背景でも読めるよう少し濃いグレーに
                            color: 'var(--gray-700, #595959)',
                            marginLeft: 8,
                          }}
                        >
                          {r.email}
                        </span>
                      ) : null}
                    </div>
                  ))}
                  {editRecipients.length > 300 && (
                    <div
                      style={{
                        padding: '4px 0',
                        color: 'var(--gray-600, #888)',
                      }}
                    >
                      …他 {editRecipients.length - 300}名（表示省略）
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* メールプレビュー（サーベイシーズン変数を反映。ユーザー変数は {$...$} のまま） */}
      <Modal
        title="メールプレビュー"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        width={760}
        destroyOnHidden
        footer={[
          <Button key="editTemplate" type="primary" onClick={goEditTemplate}>
            テンプレを修正する
          </Button>,
          <Button key="close" onClick={() => setPreviewOpen(false)}>
            閉じる
          </Button>,
        ]}
      >
        {previewLoading || !previewData ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8, color: 'var(--gray-600, #888)' }}>
              ※ サーベイシーズン管理の変数は反映済み。氏名・ID・パスワード等の
              ユーザー個別変数は「{'{$…$}'}」のまま表示されます。
            </div>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="件名">
                {previewData.subject}
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <div
              style={{
                border: '1px solid var(--gray-200, #eee)',
                borderRadius: 4,
                padding: 16,
                maxHeight: 420,
                overflowY: 'auto',
                background: '#fff',
              }}
              dangerouslySetInnerHTML={{ __html: previewData.html }}
            />
          </>
        )}
      </Modal>
    </PageContainer>
  );
};

export default MailReservateListPage;
